import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";
import { HeroIllo, UIUXIllo } from "../components/AnimatedIllustrations";

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const OFFERINGS = [
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M15.75 15.75l-2.489-2.489m0 0a3.375 3.375 0 1 0-4.773-4.773 3.375 3.375 0 0 0 4.774 4.774zM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
      </svg>
    ),
    title: "User Research",
    desc: "In-depth interviews, contextual enquiry, competitive audits, and heuristic evaluations — we find the real problem before designing the solution.",
    color: "border-rose-500/30 bg-rose-500/5",
    accent: "text-rose-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75z" />
      </svg>
    ),
    title: "Information Architecture",
    desc: "Card sorting, tree testing, and navigation design that makes complex products feel intuitive — even to first-time users.",
    color: "border-pink-500/30 bg-pink-500/5",
    accent: "text-pink-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
      </svg>
    ),
    title: "Wireframing & Prototyping",
    desc: "Low-fidelity sketches to clickable high-fidelity prototypes in Figma. We validate concepts with real users before dev begins.",
    color: "border-fuchsia-500/30 bg-fuchsia-500/5",
    accent: "text-fuchsia-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42" />
      </svg>
    ),
    title: "Visual & Brand Design",
    desc: "Typography, colour systems, iconography, and motion design that express your brand and build trust at every touchpoint.",
    color: "border-violet-500/30 bg-violet-500/5",
    accent: "text-violet-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25zM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25z" />
      </svg>
    ),
    title: "Design Systems",
    desc: "Figma component libraries with design tokens, interactive states, and developer handoff docs — so design and code stay in sync.",
    color: "border-indigo-500/30 bg-indigo-500/5",
    accent: "text-indigo-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 1-6.23-.693L4.2 13.9m15.6 1.4L21 18m-1.2-2.7-.566-.141M4.2 13.9 3 18m1.2-4.1.566-.141" />
      </svg>
    ),
    title: "Usability Testing",
    desc: "Moderated and unmoderated testing sessions, A/B experiments, and heatmap analysis to continuously improve conversion and retention.",
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
  },
];

const PROCESS = [
  {
    step: "01",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M15.75 15.75l-2.489-2.489m0 0a3.375 3.375 0 1 0-4.773-4.773 3.375 3.375 0 0 0 4.774 4.774zM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
      </svg>
    ),
    title: "Discover",
    desc: "Stakeholder interviews, user research, and competitive analysis to define the design challenge clearly.",
  },
  {
    step: "02",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
      </svg>
    ),
    title: "Ideate",
    desc: "Collaborative workshops, user journey mapping, and rapid concept sketching to explore the solution space.",
  },
  {
    step: "03",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
      </svg>
    ),
    title: "Design",
    desc: "Wireframes and high-fidelity mockups built in Figma, with a shared design system from day one.",
  },
  {
    step: "04",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zm-7.518-.267A8.25 8.25 0 1 1 20.25 10.5M8.288 14.212A5.25 5.25 0 1 1 17.25 10.5" />
      </svg>
    ),
    title: "Prototype",
    desc: "Interactive Figma prototypes that simulate real user flows — shareable, testable, dev-ready.",
  },
  {
    step: "05",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 1-6.23-.693L4.2 13.9m15.6 1.4L21 18m-1.2-2.7-.566-.141M4.2 13.9 3 18m1.2-4.1.566-.141" />
      </svg>
    ),
    title: "Test & Iterate",
    desc: "User testing sessions, quantitative analysis, and rapid design iterations until the metrics prove it works.",
  },
];

const TOOLS = [
  "Figma", "FigJam", "Maze", "Hotjar", "Lottie", "Principle",
  "Storybook", "Zeroheight", "Mixpanel", "Optimal Workshop", "UserTesting", "Framer",
];

const FEATURES = [
  "WCAG 2.1 AA accessibility",
  "Mobile-first responsive design",
  "Design tokens & variables",
  "Component library handoff",
  "Prototype user testing",
  "A/B testing framework",
  "Motion & micro-interactions",
  "Dark mode support",
  "RTL language support",
  "Developer handoff docs",
  "Design QA review",
  "Post-launch iteration",
];

export default function UIUXDetail() {
  useSEO({
    title: "UI/UX Design Services in Texas | BerryByte",
    description: "UI/UX design services in Texas — user research, custom UI design, Figma prototyping & design systems that convert. Human-centered UX from BerryByte.",
    keywords: "ui ux design services in texas, user experience designer texas, custom ui design services texas, user experience design texas, website ux design services texas, Figma design, design systems",
    canonical: "https://www.berrybyteinn.com/services/uiux-design",
  });

  return (
    <SharedLayout>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-rose-600 -top-32 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-violet-700 bottom-0 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        {/* decorative animated illustration */}
        <HeroIllo>
          <UIUXIllo />
        </HeroIllo>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/services" className="inline-flex items-center gap-2 text-slate-500 hover:text-rose-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Services / UI/UX Design
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
              Figma · Research · Design Systems · Prototyping
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              Design That<br />
              <span className="bg-gradient-to-r from-rose-400 via-pink-400 to-fuchsia-400 bg-clip-text text-transparent">Actually Converts.</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mb-10">
              Human-centered design that your users love and your business metrics reflect. We research, prototype, test, and iterate — resulting in interfaces that are beautiful and measurably effective.
            </p>
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-rose-600 to-violet-600 text-white font-semibold shadow-2xl shadow-rose-900/40 hover:shadow-rose-900/60 hover:-translate-y-1 transition-all duration-300">
              Start Your Design<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12 border-t border-white/5">
              {[{ value: "200+", label: "Products Designed" }, { value: "40%", label: "Avg Conversion Lift" }, { value: "WCAG AA", label: "Accessibility Standard" }, { value: "48hr", label: "First Prototype" }].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-black text-white mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] bg-rose-700 -top-24 -right-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-rose-400 font-semibold text-sm tracking-widest uppercase">What We Offer</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Every design discipline,<br /><span className="bg-gradient-to-r from-rose-400 to-violet-400 bg-clip-text text-transparent">done with craft.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {OFFERINGS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className={`rounded-2xl border ${item.color} p-7 hover:-translate-y-1 transition-transform duration-300`}>
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${item.accent} mb-4`}><Icon /></div>
                  <h3 className={`text-base font-bold mb-2 ${item.accent}`}>{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-fuchsia-400 font-semibold text-sm tracking-widest uppercase">Our Design Process</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Research first.<br /><span className="bg-gradient-to-r from-rose-400 to-fuchsia-400 bg-clip-text text-transparent">Design second.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.step} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 hover:-translate-y-1 transition-transform duration-300">
                  <div className="inline-flex p-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 mb-4"><Icon /></div>
                  <div className="text-xs font-bold text-rose-500 tracking-widest mb-2">{step.step}</div>
                  <h3 className="text-sm font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-xs leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-rose-400 font-semibold text-sm tracking-widest uppercase">Tools</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Our design toolkit.</h2>
              <div className="flex flex-wrap gap-3">
                {TOOLS.map((t) => (
                  <span key={t} className="px-4 py-2 rounded-xl border border-rose-500/20 bg-rose-500/5 text-rose-300 text-sm font-medium hover:bg-rose-500/10 transition-colors">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-fuchsia-400 font-semibold text-sm tracking-widest uppercase">What's Included</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Design that ships.</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-rose-500/20 border border-rose-500/30 flex items-center justify-center text-rose-400"><Check /></span>
                    <span className="text-slate-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-rose-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">Got a product<br /><span className="bg-gradient-to-r from-rose-400 to-violet-400 bg-clip-text text-transparent">that needs love?</span></h2>
          <p className="text-slate-400 text-lg mb-10 leading-relaxed">Book a free design consultation. We'll review your product and tell you exactly where UX is costing you conversions.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-rose-600 to-violet-600 text-white font-semibold shadow-2xl shadow-rose-900/40 hover:shadow-rose-900/60 hover:-translate-y-1 transition-all duration-300">
              Get a Free Design Review<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Explore Other Services
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
